import { Fragment } from 'react'
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material'

/**
 * PRISM iEDD attribution — "what moved this date", per predicted head.
 *
 * Structurally a sibling of Netra's RtoContributions (one shared row component,
 * a dense variant, an explicit units legend) so the two explainability surfaces
 * read as one product. Deliberately kept in PRISM's own folder rather than
 * imported from pages/netra: the two systems stay separate.
 *
 * Two things differ from the Netra row, both forced by the data:
 *
 * 1. **Diverging, not left-anchored.** PRISM contributions are SIGNED — a group
 *    can pull the promise earlier. A 0–100% share bar cannot show that, so the
 *    bar grows either side of a zero baseline.
 * 2. **Hours, not share-of-movement %.** Netra normalises because log-odds are
 *    unreadable. PRISM's contributions are already in the unit of the answer and
 *    sum to it, so normalising would discard the property that makes them
 *    checkable. The bar length already carries relative magnitude.
 *
 * Colours are NOT Netra's red/green: on this page green and red are bound to the
 * on-time rate bands, and one hue must not mean two things. This pair is
 * validated for CVD separation against the panel surface (ΔE 29.9 protan).
 */
const LATER = '#C2410C'   // pushes the promise later
const EARLIER = '#1D4ED8' // pulls it earlier
const MID = 'rgba(0,0,0,0.25)'
const BLUE = '#1A3C6E'
const TEAL = '#00A3A6'
const AMBER = '#B45309'

const hrs = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}h`)
const sHrs = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}h`)
const dstr = (v) => (v == null ? '—' : String(v).replace('T', ' ').slice(0, 16))

/**
 * One group's contribution, as a bar either side of a zero baseline. The signed
 * value is always printed, so polarity never rests on colour alone.
 */
export function ContributionRow({ label, hours, max, dense }) {
  const frac = max > 0 ? Math.min(Math.abs(hours) / max, 1) : 0
  const later = hours > 0
  const h = dense ? 6 : 10
  return (
    <Tooltip arrow title={`${label}: ${sHrs(hours)} — ${later ? 'pushes the promise later' : 'pulls it earlier'}`}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: dense ? 0.15 : 0.3 }}>
        <Typography variant="caption" noWrap
          sx={{ width: dense ? 150 : 220, flexShrink: 0, color: 'text.secondary' }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: dense ? 80 : 130, display: 'flex', alignItems: 'center', height: h + 4 }}>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            {!later && <Box sx={{ width: `${frac * 100}%`, height: h, bgcolor: EARLIER, borderRadius: '4px 0 0 4px' }} />}
          </Box>
          <Box sx={{ width: '1px', height: h + 4, bgcolor: MID, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            {later && <Box sx={{ width: `${frac * 100}%`, height: h, bgcolor: LATER, borderRadius: '0 4px 4px 0' }} />}
          </Box>
        </Box>
        <Typography variant="caption"
          sx={{ width: 56, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {sHrs(hours)}
        </Typography>
      </Stack>
    </Tooltip>
  )
}

/**
 * One head's attribution. Reconciles by construction —
 *   base + Σ(groups) + calibration = predicted
 * — so the panel shows the arithmetic instead of asking for trust.
 */
export function HeadExplain({ title, ex, dense }) {
  if (!ex) return null
  const max = Math.max(...ex.groups.map((g) => Math.abs(g.hours)), 0.0001)
  return (
    <Box sx={{ mb: dense ? 1 : 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.25, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: BLUE, fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {ex.head} · P{ex.quantile} · typical shipment {hrs(ex.base_hours)} → this one {hrs(ex.predicted_hours)}
        </Typography>
      </Stack>
      {ex.groups.map((g) => (
        <ContributionRow key={g.key} label={g.label} hours={g.hours} max={max} dense={dense} />
      ))}
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          conformal calibration <b>{sHrs(ex.calibration_hours)}</b> (×{ex.calibration_factor})
        </Typography>
        {ex.top_features?.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            top columns: {ex.top_features.map((f) => `${f.feature} ${sHrs(f.hours)}`).join(' · ')}
          </Typography>
        )}
      </Stack>
      {ex.monotonised_to_hours != null && (
        <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
          This rung crossed a lower one and was raised to {hrs(ex.monotonised_to_hours)}; the breakdown
          above explains the head&apos;s own {hrs(ex.predicted_hours)}.
        </Typography>
      )}
    </Box>
  )
}

/** The arithmetic that produced the date: anchor + each term = the promise. */
export function Composition({ c }) {
  if (!c) return null
  const parts = [
    ['anchor', dstr(c.anchor)],
    ...(c.wh_hours != null ? [['warehouse', hrs(c.wh_hours)]] : []),
    // mobility_hours is the term actually in the date (it already carries any
    // overlay delta on rows stored before overlays were removed)
    [c.source === 'ndr' ? 'NDR re-attempt' : 'transit', hrs(c.mobility_hours)],
  ]
  return (
    <Box sx={{ mb: 1.25 }}>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        {parts.map(([k, v], i) => (
          <Fragment key={k}>
            {i > 0 && <Typography variant="caption" color="text.secondary">+</Typography>}
            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)' }}>
              <Typography variant="caption" color="text.secondary">{k}</Typography>{' '}
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{v}</Typography>
            </Box>
          </Fragment>
        ))}
        <Typography variant="caption" color="text.secondary">=</Typography>
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(0,163,166,0.1)', border: `1px solid ${TEAL}` }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{hrs(c.total_promised)}</Typography>
        </Box>
      </Stack>
      {c.source === 'fallback' && (
        <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 0.5 }}>
          Fallback: this date is the courier&apos;s published TAT plus a safety buffer, not a model
          output — so PRISM publishes no attribution for it.
        </Typography>
      )}
    </Box>
  )
}

/** Units legend — mirrors the footer Netra's dialog carries, in PRISM's units. */
export function ContributionLegend() {
  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" display="block">
        Bars are <b>hours</b>, not percentages — the same unit as the promise, so they add up to it:
        base + every bar + calibration = the predicted hours.
        <br />
        They are <b>deviations from a typical shipment</b>, not causes summing from zero.
        <Box component="span" sx={{ color: LATER, fontWeight: 700 }}> Orange</Box> pushes the date later ·
        <Box component="span" sx={{ color: EARLIER, fontWeight: 700 }}> blue</Box> pulls it earlier.
        <br />
        <b>Conformal calibration</b> is the correction that makes the quantile hold empirically. It is a
        real part of the promise, so it is its own line rather than folded into the bars.
        <br />
        Recorded when the promise was made — older shipments predate this and show nothing, because
        re-deriving it now would explain today&apos;s data, not the promise that was given.
      </Typography>
    </>
  )
}
